// src/services/protocolsService.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE = "/protocols";

/* ========== helpers ========== */
const jsonOrThrow = async (res) => {
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error || data.detail)) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const trim = (x) => (x == null ? "" : String(x).trim());
const toList = (v) => {
  if (Array.isArray(v)) return v.map((t) => trim(t)).filter(Boolean);
  if (v == null) return [];
  return String(v)
    .split(/\r?\n/)
    .map((t) => t.trim())
    .filter(Boolean);
};

// Normaliza el objeto que viene del backend a { lastReview, principles, protocols: [...] }
const normalizeFromServer = (obj = {}) => {
  const src = Array.isArray(obj.protocols)
    ? obj.protocols
    : Array.isArray(obj.sections)
    ? obj.sections
    : [];

  const protocols = src.map((s) => ({
    title: trim(s.title) || "Protocolo",
    icon: trim(s.icon) || "",
    iconImageUrl: trim(s.iconImageUrl) || "",
    // Aceptar ambos nombres (nuevo/legacy)
    summary: Array.isArray(s.summary)
      ? s.summary
      : Array.isArray(s.body)
      ? s.body
      : [],
    steps: Array.isArray(s.steps)
      ? s.steps
      : Array.isArray(s.bullets)
      ? s.bullets
      : [],
    // CTA tal como viene (backend ya lo normaliza a {label,url})
    cta: s.cta || undefined,
    // por si quieres usarlo en el front para ocultar/mostrar principios
    showPrinciples:
      typeof s.showPrinciples === "boolean" ? s.showPrinciples : undefined,
  }));

  return {
    lastReview: trim(obj.lastReview),
    principles: Array.isArray(obj.principles)
      ? obj.principles.map((p) => trim(p)).filter(Boolean)
      : [],
    protocols,
  };
};

// Normaliza el payload del front a lo que espera el backend: { lastReview, principles?, sections: [...] }
const normalizeToServer = (payload = {}) => {
  const src = Array.isArray(payload.protocols)
    ? payload.protocols
    : Array.isArray(payload.sections)
    ? payload.sections
    : Array.isArray(payload.content)
    ? payload.content
    : [];

  const sections = src
    .map((p) => {
      const title = trim(p.title) || "Protocolo";
      const icon = trim(p.icon);
      const iconImageUrl = trim(p.iconImageUrl);
      const body = toList(p.body ?? p.summary);
      const bullets = toList(p.bullets ?? p.steps);

      // CTA: aceptar variantes del form
      const hasCta =
        p?.cta &&
        (p.cta.url ||
          p.cta.contactUrl ||
          p.cta.policyUrl ||
          p.cta.label);

      const cta = hasCta
        ? {
            ...(p.cta.label ? { label: trim(p.cta.label) } : {}),
            ...(p.cta.url ? { url: trim(p.cta.url) } : {}),
            ...(p.cta.contactUrl
              ? { contactUrl: trim(p.cta.contactUrl) }
              : {}),
            ...(p.cta.policyUrl
              ? { policyUrl: trim(p.cta.policyUrl) }
              : {}),
          }
        : undefined;

      const out = { title };
      if (icon) out.icon = icon;
      if (iconImageUrl) out.iconImageUrl = iconImageUrl;
      // usamos nombres legacy que el backend ya acepta (summary/steps)
      if (body.length) out.summary = body;
      if (bullets.length) out.steps = bullets;
      if (typeof p.showPrinciples === "boolean") {
        out.showPrinciples = p.showPrinciples;
      }
      if (cta) out.cta = cta;

      return out;
    })
    .filter(
      (it) =>
        it &&
        (it.title ||
          (it.summary && it.summary.length) ||
          (it.steps && it.steps.length) ||
          it.cta)
    );

  const result = {
    lastReview: trim(payload.lastReview),
    sections, // ← mandamos sections para máxima compatibilidad
  };

  // Solo mandamos principles si el front los controla
  if (Array.isArray(payload.principles)) {
    result.principles = payload.principles
      .map((p) => trim(p))
      .filter(Boolean);
  }

  return result;
};

/* ========== Docs ========== */
export const listProtocolDocs = async () => {
  const res = await fetchWithAuth(`${BASE}`, { method: "GET" });
  const arr = await jsonOrThrow(res);
  return Array.isArray(arr)
    ? arr.map((m) => ({
        id: m.id || m._id,
        title: m.title,
        versionTag: m.versionTag,
        isPublished: !!m.isPublished,
        createdAt: m.createdAt,
        secureUrl: m.secureUrl,
        coverUrl: m.coverUrl || null,
        bytes: m.bytes,
      }))
    : [];
};

export const uploadProtocolDoc = async ({
  file,
  title,
  versionTag,
  isPublished = true,
}) => {
  if (!file) throw new Error("Archivo requerido");
  const fd = new FormData();
  fd.append("file", file, file.name || "protocol.pdf");
  if (title) fd.append("title", trim(title));
  if (versionTag) fd.append("versionTag", trim(versionTag));
  fd.append("isPublished", String(!!isPublished));
  const res = await fetchWithAuth(`${BASE}/upload`, {
    method: "POST",
    body: fd,
  });
  return jsonOrThrow(res);
};

export const updateProtocolDoc = async (id, payload = {}) => {
  const res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res);
};

export const deleteProtocolDoc = async (id) => {
  const res = await fetchWithAuth(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  await jsonOrThrow(res);
  return true;
};

export const downloadProtocolDocUrl = (id) =>
  id ? `${BASE}/${encodeURIComponent(id)}/download` : "#";

export const getLatestProtocolDoc = async () => {
  const res = await fetchWithAuth(`${BASE}/latest`, { method: "GET" });
  const m = await jsonOrThrow(res);
  if (!m) return null;
  return {
    id: m.id || m._id,
    title: m.title,
    versionTag: m.versionTag,
    isPublished: !!m.isPublished,
    createdAt: m.createdAt,
    secureUrl: m.secureUrl,
    coverUrl: m.coverUrl || null,
    bytes: m.bytes,
  };
};

/* ========== Config ========== */
export const getProtocolsConfig = async () => {
  try {
    const res = await fetchWithAuth(`${BASE}/config`, { method: "GET" });
    return await jsonOrThrow(res);
  } catch {
    // fallback muy básico
    return { pageTitle: "Protocolos", subtitle: "" };
  }
};

export const updateProtocolsConfig = async (payload) => {
  // Solo lo permitido: pageTitle + subtitle (el backend ignora extras)
  const body = {};
  if (payload.pageTitle != null) body.pageTitle = trim(payload.pageTitle);
  if (payload.subtitle != null) body.subtitle = trim(payload.subtitle);
  // OJO: no mandamos lastUpdatedAt ni otras llaves que no existen en el DTO

  const res = await fetchWithAuth(`${BASE}/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
};

/* ========== Content ========== */
export const getProtocolsContent = async () => {
  const res = await fetchWithAuth(`${BASE}/content`, { method: "GET" });
  const raw = await jsonOrThrow(res);
  return normalizeFromServer(raw);
};

export const updateProtocolsContent = async (_payload) => {
  const body = normalizeToServer(_payload);
  const res = await fetchWithAuth(`${BASE}/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const saved = await jsonOrThrow(res);
  // devolvemos ya normalizado para que el front no tenga que adivinar
  return normalizeFromServer(saved);
};
